/**
 * reminderTimeDisplay.ts — pure helper that shows the daysheet-reminder send
 * hour in BOTH the server clock and the venue's local clock.
 *
 * Why: `show_reminder_settings.reminder_hour` is compared to the SERVER's local
 * hour (server/index.js sweep + server/lib/reminders.js `ref.getHours()`), so
 * the time the user picks is a server time. When the venue sits in another
 * timezone the person reading the card has no idea what that hour means on
 * site. This helper renders the equivalent local time at the venue.
 *
 * DST-correct by construction: the venue time is computed with Intl for the
 * SEND date (never a hard-coded +1/+2, which would be wrong twice a year).
 * The configured hour is taken on the server clock (UTC reference); see
 * references/reminder-time-display.md for the assumption and its follow-up.
 *
 * No DOM, no side effects — unit-testable as-is.
 */

export interface VenueLocalTime {
  /** "HH:MM" in the venue's timezone, 24-hour clock. */
  hhmm: string;
  /** The IANA timezone the value was rendered for. */
  tz: string;
}

/**
 * The venue's local clock time for a send happening at `serverHour:00` on
 * `sendDate` (defaults to today).
 *
 * @returns { hhmm, tz } or null when no usable timezone is given (the caller
 *          then shows the server time alone).
 */
export function venueLocalTimeFor(
  serverHour: number,
  venueTz?: string | null,
  sendDate?: Date,
): VenueLocalTime | null {
  if (!venueTz) return null;
  if (!Number.isFinite(serverHour) || serverHour < 0 || serverHour > 23) return null;

  const base = sendDate instanceof Date && !Number.isNaN(sendDate.getTime())
    ? sendDate
    : new Date();
  const instant = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), Math.floor(serverHour), 0, 0, 0),
  );

  try {
    const hhmm = new Intl.DateTimeFormat('en-GB', {
      timeZone: venueTz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(instant);
    return { hhmm, tz: venueTz };
  } catch {
    // Unknown/invalid IANA zone → no local equivalent to show.
    return null;
  }
}

/** "HH:MM" for a whole-hour number (e.g. 6 → "06:00"). */
export function formatServerHour(serverHour: number): string {
  const h = Number.isFinite(serverHour) ? Math.max(0, Math.min(23, Math.floor(serverHour))) : 0;
  return `${String(h).padStart(2, '0')}:00`;
}
